import { COMPARE_ITEM_ID } from '@pn/core/domain/production';
import {
  areFiltersApplied,
  type Filter,
  type LinkOperator,
} from '@pn/core/domain/query';
import {
  createWorkspaceItem,
  isDynamicWorkspaceItem,
  isSavedStaticList,
  type WorkspaceItem,
} from '@pn/core/domain/workspace';
import { useVisualizeWorkspaceItem } from '@pn/core/operations/workspace';
import { filtersToTitle } from '@pn/core/operations/workspace/filtersToTitle';
import { getNextFilterColor } from '@pn/core/operations/workspace/getNextFilterColor';
import {
  useAppStorage,
  useCurrentUserStorage,
  useWorkspaceStorage,
  workspaceActions,
} from '@pn/core/storage';
import { getMapping } from '@pn/core/storage/workspace/mappings';
import { assert } from '@pn/core/utils/assert';
import { log } from '@pn/core/utils/debug';
import isEqual from 'fast-deep-equal';
import { isEmpty, isNil } from 'lodash-es';
import { useParams } from 'react-router-dom';

type Props = {
  tableFields?: Record<string, string[] | undefined>;
  tableOnly?: boolean;
};

type Params = {
  filters: Filter[];
  filtersLinkOperator: LinkOperator;
  item: WorkspaceItem;
};

export function useFilterWorkspaceItem({
  tableFields,
  tableOnly = false,
}: Props) {
  const { allWorkspaceItems } = useWorkspaceStorage();

  const getTempItem = useTemporaryItem();
  const visualizeWorkspaceItem = useVisualizeWorkspaceItem({
    tableFields,
    tableOnly,
  });

  return async ({ filters, filtersLinkOperator, item }: Params) => {
    /**
     * DataTable disables filtering for saved dynamic lists (see
     * `isSavedDynamicList`); their filters are edited in QueryBuilderDialog.
     */
    if (
      item.itemType === 'list' &&
      !item.isTemporary &&
      isDynamicWorkspaceItem(item)
    ) {
      return;
    }

    const sourceItem = item.sourceItem ?? item;
    const temporaryItem = getTempItem({ filters, filtersLinkOperator, item });

    if (areFiltersApplied(filters)) {
      if (!isEmpty(sourceItem.query.clickedIds)) {
        /**
         * Reset single selection when applying non-empty filters.
         * Multi-selection (box selection, lists, etc.) will not be affected.
         */
        workspaceActions().updateClickedIds(sourceItem.id, []);
        workspaceActions().updatePage(sourceItem.id, 0);
      }

      temporaryItem.query.page = 0;

      workspaceActions().add(temporaryItem);
      workspaceActions().addToWorkspace(temporaryItem.id);

      log().info('visualize temporary filtered workspace item', temporaryItem);
      await visualizeWorkspaceItem({ item: temporaryItem });

      workspaceActions().select(temporaryItem.id);
    } else {
      /**
       * The exact item this filter item was created from.
       * Can be undefined if it gets removed between applying the filters and
       * clearing them.
       */
      const temporarySourceItem = allWorkspaceItems[
        temporaryItem.temporarySourceItemId
      ] as WorkspaceItem | undefined;

      if (item.id === temporaryItem.id) {
        if (shouldRemoveTemporaryItem(temporaryItem, temporarySourceItem)) {
          workspaceActions().remove(temporaryItem);
          workspaceActions().select(temporaryItem.temporarySourceItemId);
        } else {
          workspaceActions().update({
            ...temporaryItem,
            query: {
              ...temporaryItem.query,
              filters,
              filtersLinkOperator,
            },
            numberOfElements: temporaryItem.query.scopeIds.length,
            isRendered: false,
            isProcessed: false,
          });
        }
      } else {
        if (isEqual(filters, item.query.filters)) return;

        /**
         * Allows to manipulate the filters in the filter panel without actually
         * applying them.
         */
        workspaceActions().updateFiltering(
          item.id,
          filters,
          filtersLinkOperator
        );
      }
    }
  };
}

type TemporaryWorkspaceItem = WorkspaceItem & {
  isTemporary: true;
  temporarySourceItemId: string;
};

function useTemporaryItem() {
  const { type } = useParams();

  const { unitSystem } = useAppStorage();
  const { user } = useCurrentUserStorage();
  const { allWorkspaceItems } = useWorkspaceStorage();

  return ({
    filters,
    filtersLinkOperator,
    item,
  }: Params): TemporaryWorkspaceItem => {
    const existingItem = allWorkspaceItems[item.id];

    const mapping = getMapping(item.dataType);
    assert(mapping, 'Mapping must be defined for filtering');

    const autogeneratedName = filtersToTitle(filters, mapping, unitSystem);
    const name =
      item.isTemporary &&
      !isEmpty(item.query.scopeIds) &&
      autogeneratedName !== ''
        ? `${autogeneratedName} from selection`
        : autogeneratedName;

    if (!isNil(existingItem) && existingItem.isTemporary) {
      return {
        ...existingItem,
        name,
        query: {
          ...existingItem.query,
          filters,
          filtersLinkOperator,
        },
        isProcessed: true,
        isRendered: true,
      } as TemporaryWorkspaceItem;
    }

    const newItem = createWorkspaceItem(
      {
        id: type === 'list_compare' ? COMPARE_ITEM_ID : undefined,
        source: 'item',
        sourceItem: item.sourceItem ?? item,
        temporarySourceItemId: item.id,
        name: autogeneratedName,
        extraStyle: { color: getNextFilterColor(allWorkspaceItems) },
        queryOptions: {
          scopeIds: item.query.scopeIds,
          /* Keeps a saved child in sync with the list rather than its ids. */
          sourceListId: isSavedStaticList(item) ? item.id : undefined,
          sorts: item.query.sorts,
          filters,
          filtersLinkOperator,
          checkedIds: item.query.checkedIds, // necessary for list_compare mode
        },
        isVisualized: true,
      },
      user
    ) as TemporaryWorkspaceItem;

    return newItem;
  };
}

function shouldRemoveTemporaryItem(
  temporaryItem: WorkspaceItem,
  temporarySourceItem: WorkspaceItem | undefined
): boolean {
  if (temporaryItem.id === COMPARE_ITEM_ID) return false;

  if (!isNil(temporaryItem.query.sql)) return false;

  /* Without filters, a bare list reference is just a copy of its source list. */
  if (
    !isNil(temporarySourceItem) &&
    temporaryItem.query.sourceListId === temporarySourceItem.id
  ) {
    return true;
  }

  /**
   * With nothing else narrowing it down, the item matches its entire base
   * layer, which is too large to show.
   */
  return isEmpty(temporaryItem.query.scopeIds);
}
